import {
    ActionIcon,
    Alert,
    Badge,
    Box,
    Button,
    Group,
    Loader,
    Menu,
    Stack,
    Text,
    ThemeIcon,
    Title,
    Tooltip,
    useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconChevronRight, IconDots, IconEdit, IconInfoCircle, IconPlus, IconTrash } from "@tabler/icons-react";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Suspense } from "react";
import { Content } from "shared/components/global/Content";
import { CategoryCreateModal } from "shared/components/organization/topics/CategoryCreateModal";
import { TopicCreateModal } from "shared/components/organization/topics/TopicCreateModal";
import { useArchiveTopic } from "shared/components/organization/topics/useArchiveTopic";
import { features } from "shared/graphql/features";
import { topicsQueryOptions } from "shared/graphql/topics";
import { useColorMode } from "shared/hooks/useColorMode";
import { usePermissionCheck } from "shared/stores/oidc";
import { logger } from "shared/utils/logger";
import { Permission, TopicLikeFragment } from "src/gql/graphql";
import { queryClient } from "src/queryClient";

const NoChildrenAlert = ({ onCreate }: { onCreate: () => void }) => (
    <Alert icon={<IconInfoCircle size={16} />} title="No topics within this category." ml="sm">
        <Stack>
            <Text>
                Add a topic to this category to get started. Topics allow you to send to subscribed users. You cannot send messages to a
                category without any topics.
            </Text>
            <Button variant="outline" onClick={onCreate}>
                Add Topic
            </Button>
        </Stack>
    </Alert>
);

const TopicDetails = ({ topic, handleTopicCreate }: { topic: TopicLikeFragment; handleTopicCreate: (categoryId: string) => void }) => {
    const nav = useNavigate();
    const theme = useMantineTheme();
    const { isDark } = useColorMode();
    const [opened, { toggle }] = useDisclosure(false);
    const { archiveTopic } = useArchiveTopic(topic, () => window.location.reload());

    const { data } = useQuery({ ...topicsQueryOptions.topicChildren(topic.id), enabled: topic.isCategory });
    const children = data?.children;

    const hasEditPermission = usePermissionCheck(Permission.TopicCategoryManage, topic.id);

    return (
        <Stack w="100%" gap={0}>
            <Group key={topic.id} w="100%" py="xs" gap={0} align="center">
                {topic.isCategory && (
                    <ActionIcon variant="transparent" onClick={toggle} ml={-6} aria-label="Show or hide subtopics">
                        <IconChevronRight
                            size={16}
                            style={{ transition: "transform 0.5s", transform: opened ? "rotate(90deg)" : "rotate(0deg)" }}
                        />
                    </ActionIcon>
                )}
                <Text px="sm">{topic.name}</Text>
                {topic.isCategory && children?.length === 0 && (
                    <Tooltip label="No topics within this category. Add topics to send to subscribed users.">
                        <ThemeIcon mr="xs" variant="transparent">
                            <IconInfoCircle color="red" size={16} />
                        </ThemeIcon>
                    </Tooltip>
                )}
                {hasEditPermission ? (
                    topic.isCategory ? (
                        <Menu withInitialFocusPlaceholder={false}>
                            <Menu.Target>
                                <ActionIcon variant="transparent" aria-label="Show topic options">
                                    <IconDots size={16} />
                                </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Item
                                    onClick={() =>
                                        modals.open({
                                            title: "Create Category",
                                            children: (
                                                <Suspense fallback={<Loader />}>
                                                    <CategoryCreateModal parentCategory={topic} />
                                                </Suspense>
                                            ),
                                        })
                                    }
                                >
                                    Create Category
                                </Menu.Item>
                                <Menu.Item onClick={() => handleTopicCreate(topic.id)}>Create Topic</Menu.Item>
                                <Menu.Item onClick={() => void nav({ to: "/organization/topics/$topicId", params: { topicId: topic.id } })}>
                                    Edit Category
                                </Menu.Item>
                                <Menu.Item
                                    color="var(--mantine-primary-color-filled)"
                                    c="var(--mantine-color-text)"
                                    onClick={() => void archiveTopic()}
                                >
                                    Archive Category
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    ) : (
                        <>
                            <ActionIcon
                                variant="transparent"
                                onClick={() => void nav({ to: "/organization/topics/$topicId", params: { topicId: topic.id } })}
                                aria-label="Edit topic"
                            >
                                <IconEdit size={16} />
                            </ActionIcon>
                            &nbsp;
                            <ActionIcon variant="subtle" onClick={() => void archiveTopic()} aria-label="Archive topic">
                                <IconTrash size={16} />
                            </ActionIcon>
                        </>
                    )
                ) : null}
                {topic.isRequired && <Badge ml="sm">Required</Badge>}
            </Group>
            {opened && children?.length === 0 && <NoChildrenAlert onCreate={() => handleTopicCreate(topic.id)} />}
            {opened &&
                children?.map((c) => (
                    <Box
                        key={c.id}
                        style={{ borderLeft: `var(--mantine-spacing-md) solid ${isDark ? theme.colors.dark[5] : theme.colors.gray[1]}` }}
                    >
                        <TopicDetails topic={c} handleTopicCreate={handleTopicCreate} />
                    </Box>
                ))}
        </Stack>
    );
};

const TopicsContainer = () => {
    const {
        data: { rootTopic },
    } = useSuspenseQuery(topicsQueryOptions.rootTopic);
    const {
        data: { children },
    } = useSuspenseQuery(topicsQueryOptions.topicChildren(rootTopic.id));

    const nav = useNavigate();
    const hasRootEditPermission = usePermissionCheck(Permission.TopicCategoryManage, rootTopic.id);

    features.useGuard("HAS_TOPICS");

    function handleTopicCreate(categoryId: string) {
        modals.open({
            title: "Topic Type",
            children: (
                <TopicCreateModal
                    onSubmit={(required) => {
                        modals.closeAll();
                        nav({
                            to: "/organization/topics/$topicId",
                            params: { topicId: "create" },
                            search: { required, category: categoryId },
                        }).catch(logger.error);
                    }}
                />
            ),
        });
    }

    return (
        <Content paper>
            <Content.Heading>
                <Group gap={0}>
                    <Title order={3} size="h1">
                        Topics
                    </Title>
                    {hasRootEditPermission && (
                        <Menu withInitialFocusPlaceholder={false}>
                            <Menu.Target>
                                <ActionIcon variant="transparent" aria-label="Show create options">
                                    <IconPlus size={16} />
                                </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Item
                                    onClick={() =>
                                        modals.open({
                                            title: "Create Category",
                                            children: (
                                                <Suspense fallback={<Loader />}>
                                                    <CategoryCreateModal parentCategory={rootTopic} />
                                                </Suspense>
                                            ),
                                        })
                                    }
                                >
                                    Create Category
                                </Menu.Item>
                                <Menu.Item onClick={() => handleTopicCreate(rootTopic.id)}>Create Topic</Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    )}
                </Group>
            </Content.Heading>
            <Stack gap={0} h="100%" align="flex-start" style={{ overflow: "auto" }}>
                {children.map((c) => (
                    <TopicDetails key={c.id} topic={c} handleTopicCreate={handleTopicCreate} />
                ))}
            </Stack>
        </Content>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/organization/topics/")({
    component: TopicsContainer,
    loader: async () => {
        await queryClient.ensureQueryData(topicsQueryOptions.rootTopic);
    },
});
